import { zodSchemes } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { z } from 'zod';

import { AccountLineItemSchemaParams } from './QBooksAccountLineItemsSection.types';

export const createSchema = (params: AccountLineItemSchemaParams) => {
    return z.object({
        lines: z.array(createAccountLineItemSchema(params)),
        lineAmountType: createAccountAmountTypeSchema(),
    });
};

export const createAccountLineItemSchema = (params: AccountLineItemSchemaParams) => {
    return z
        .object({
            index: z.number(),

            id: z.string(),
            checked: z.boolean().optional(),
            valid: z.boolean(),
            empty: z.boolean(),
            canBeBillable: z.boolean(),
            hideRemove: z.boolean(),
            hideClone: z.boolean(),
            isNew: z.boolean(),
            isBillable: z.boolean().optional(),
            isTaxable: z.boolean().optional(),
            isMatched: z.boolean().optional(),
            description: z.string(),
            amount: z.number({ message: '' }).min(0).optional(),

            matchedLineItemId: z.string().optional().nullable(),
            matchedLineItemIds: z.array(z.string()).optional(),

            remainingBalance: z.number().optional(),
            remainingBalanceForRequest: z.number().optional(),

            classId: zodSchemes.optional.select.string.nullable(),
            taxCodeId: zodSchemes.optional.select.string.nullable(),
            accountId: zodSchemes.optional.select.string.nullable(),
            customerId: zodSchemes.optional.select.string.nullable(),
        })
        .superRefine((line, context) => {
            const {
                showValidationErrors = false,
                isTrackingTaxesEnabled = false,
                isClassMandatory = false,
                isCustomerMandatory = false,
            } = params;

            if (!showValidationErrors || line.empty) {
                return;
            }

            if (!line.accountId) {
                context.addIssue({
                    path: ['accountId'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (line.accountId && !!line.isBillable && !line.canBeBillable) {
                context.addIssue({
                    path: ['accountId'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (isTrackingTaxesEnabled && showValidationErrors && !line.taxCodeId) {
                context.addIssue({
                    path: ['taxCodeId'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (line.accountId && line.isBillable && !line.canBeBillable) {
                context.addIssue({
                    path: ['isBillable'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (isCustomerMandatory && !line.customerId) {
                context.addIssue({
                    path: ['customerId'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (isClassMandatory && !line.classId) {
                context.addIssue({
                    path: ['classId'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }

            if (line.amount === null) {
                context.addIssue({
                    path: ['amount'],
                    code: z.ZodIssueCode.custom,
                    message: '',
                });
            }
        });
};

export const createAccountAmountTypeSchema = () => {
    return z.nativeEnum(domain.LineAmountType).nullable();
};
